import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

// API URL from environment or default
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const BulkCreateUnitsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  
  // Get site info from query params
  const siteUuid = queryParams.get('siteUuid');
  const siteName = queryParams.get('siteName');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [previewUnits, setPreviewUnits] = useState([]);
  
  // State to track job progress
  const [jobProgress, setJobProgress] = useState(null);
  const [pollingCount, setPollingCount] = useState(0);
  
  // Basic range settings - Updated
  const [startUnitId, setStartUnitId] = useState(1);
  const [numberOfUnits, setNumberOfUnits] = useState(20);
  
  // Status setting
  const [status, setStatus] = useState('ACTIVE');
  
  // Is Prepaid setting
  const [isPrepaid, setIsPrepaid] = useState(false);
  
  // Copy Unit ID to Hardware ID setting
  const [copyUnitIdToHardwareId, setCopyUnitIdToHardwareId] = useState(true);
  
  // Display name prefix settings (based on Unit ID)
  const [prefixRules, setPrefixRules] = useState([
    { startId: 1, endId: 20, prefix: '' }
  ]);
  
  // Display name overrides (based on Unit ID) - New State
  const [displayNameOverrides, setDisplayNameOverrides] = useState([
    { unitId: '', displayName: '' } // Start with one empty row
  ]);
  
  // Display name suffix settings (based on Unit ID) - New State
  const [suffixRules, setSuffixRules] = useState([
      { startId: '', endId: '', suffix: '' } // Start with one empty rule
  ]);
  
  // Unit Number Mapping Rules - New State
  const [unitNumberMappingRules, setUnitNumberMappingRules] = useState([
    { startUnitId: '', endUnitId: '', startUnitNumber: '', skipNumbers: '' } // Start with one empty rule
  ]);
  
  useEffect(() => {
    // Validate that we have site information
    if (!siteUuid) {
      setError('Missing site information. Please select a site first.');
    }
  }, [siteUuid]);
  
  // Clean up polling interval on component unmount
  useEffect(() => {
    return () => {
      if (window.pollIntervalId) {
        clearInterval(window.pollIntervalId);
      }
    };
  }, []);
  
  // Add a new prefix rule
  const addPrefixRule = () => {
    setPrefixRules([
      ...prefixRules,
      { startId: 1, endId: 20, prefix: '' }
    ]);
  };
  
  // Remove a prefix rule
  const removePrefixRule = (index) => {
    setPrefixRules(prefixRules.filter((_, i) => i !== index));
  };
  
  // Update a prefix rule
  const updatePrefixRule = (index, field, value) => {
    const updatedRules = [...prefixRules];
    updatedRules[index][field] = value;
    setPrefixRules(updatedRules);
  };
  
  // --- New functions for Display Name Overrides ---
  
  // Add a new override rule
  const addOverrideRule = () => {
    setDisplayNameOverrides([
      ...displayNameOverrides,
      { unitId: '', displayName: '' }
    ]);
  };
  
  // Remove an override rule
  const removeOverrideRule = (index) => {
    setDisplayNameOverrides(displayNameOverrides.filter((_, i) => i !== index));
  };
  
  // Update an override rule
  const updateOverrideRule = (index, field, value) => {
    const updatedOverrides = [...displayNameOverrides];
    // Ensure unitId is stored as a number if not empty
    if (field === 'unitId') {
      updatedOverrides[index][field] = value === '' ? '' : parseInt(value, 10);
    } else {
      updatedOverrides[index][field] = value;
    }
    setDisplayNameOverrides(updatedOverrides);
  };
  
  // --- End of new functions ---
  
  // --- New functions for Display Name Suffixes ---
  
  const addSuffixRule = () => {
    setSuffixRules([
      ...suffixRules,
      { startId: '', endId: '', suffix: '' }
    ]);
  };
  
  const removeSuffixRule = (index) => {
    setSuffixRules(suffixRules.filter((_, i) => i !== index));
  };
  
  const updateSuffixRule = (index, field, value) => {
    const updatedSuffixes = [...suffixRules];
    // Ensure IDs are stored as numbers if not empty
    if (field === 'startId' || field === 'endId') {
      updatedSuffixes[index][field] = value === '' ? '' : parseInt(value, 10);
    } else {
      updatedSuffixes[index][field] = value;
    }
    setSuffixRules(updatedSuffixes);
  };

  // --- End of new suffix functions ---
  
  // --- New functions for Unit Number Mapping Rules ---

  const addMappingRule = () => {
    setUnitNumberMappingRules([
      ...unitNumberMappingRules,
      { startUnitId: '', endUnitId: '', startUnitNumber: '', skipNumbers: '' }
    ]);
  };

  const removeMappingRule = (index) => {
    setUnitNumberMappingRules(unitNumberMappingRules.filter((_, i) => i !== index));
  };

  const updateMappingRule = (index, field, value) => {
    const updatedMappings = [...unitNumberMappingRules];
    // Ensure numeric fields are stored as numbers if not empty
    if (field === 'startUnitId' || field === 'endUnitId' || field === 'startUnitNumber') {
      updatedMappings[index][field] = value === '' ? '' : parseInt(value, 10);
    } else {
      updatedMappings[index][field] = value; // skipNumbers is text
    }
    setUnitNumberMappingRules(updatedMappings);
  };

  // --- End of mapping rule functions ---
  
  // Start polling job status
  const pollJobStatus = useCallback((jobId) => {
    // Clear existing interval if any
    if (window.pollIntervalId) {
      clearInterval(window.pollIntervalId);
    }
    
    window.pollIntervalId = setInterval(async () => {
      try {
        const statusResponse = await axios.get(
          `${API_URL}/units/bulk/${jobId}`,
          {
            withCredentials: true // Using cookie-based authentication
          }
        );
        
        setJobProgress(statusResponse.data);
        setPollingCount(prev => {
          const newCount = prev + 1;
          
          // If job is completed or failed, or we've reached timeout
          if (
            statusResponse.data.status === 'COMPLETED' ||
            statusResponse.data.status === 'FAILED' ||
            newCount >= 10
          ) {
            clearInterval(window.pollIntervalId);
            
            if (statusResponse.data.status === 'COMPLETED') {
              setSuccess(true);
              
              // No automatic redirection anymore
              setLoading(false);
            } else if (statusResponse.data.status === 'FAILED') {
              setError(`Job failed: ${statusResponse.data.errorMessage || 'Unknown error'}`);
              setLoading(false);
            } else if (newCount >= 10) {
              // Timeout reached
              setError('Timeout reached while checking job status. The operation may still be processing.');
              setLoading(false);
            }
          }
          
          return newCount;
        });
      } catch (err) {
        clearInterval(window.pollIntervalId);
        // console.error('Error polling job status:', err);
        setError('Failed to check job status: ' + (err.response?.data?.message || err.message));
        setLoading(false);
      }
    }, 8000); // Poll every 8 seconds instead of every second
  }, [API_URL, navigate]);
  
  // Generate preview of units based on settings
  const generatePreview = () => {
    // Clear any previous errors
    setError(null);
    
    // Basic validation
    if (!numberOfUnits || numberOfUnits <= 0) {
        setError('Number of units must be greater than 0.');
        return;
    }
    if (!startUnitId || startUnitId < 1) {
        setError('Start Unit ID must be 1 or greater.');
        return;
    }

    const calculatedEndUnitId = startUnitId + numberOfUnits - 1;

    // Validate prefix rules against the *calculated* unit ID range
    const prefixValidationError = validatePrefixRules(startUnitId, calculatedEndUnitId); // Pass range to validator
    if (prefixValidationError) {
      setError(prefixValidationError);
      return;
    }
    
    // Validate suffix rules against the *calculated* unit ID range - New
    const suffixValidationError = validateSuffixRules(startUnitId, calculatedEndUnitId);
    if (suffixValidationError) {
        setError(suffixValidationError);
        return;
    }
    
    // Validate Unit Number Mapping Rules - New
    const mappingValidationError = validateMappingRules(startUnitId, calculatedEndUnitId);
    if (mappingValidationError) {
        setError(mappingValidationError);
        return;
    }

    // Pre-process mapping rules for easier lookup and skip list parsing
    const processedMappingRules = unitNumberMappingRules.map(rule => {
        const skipSet = new Set();
        if (rule.skipNumbers && rule.skipNumbers.trim()) {
            try {
                const parts = rule.skipNumbers.split(',').map(part => part.trim());
                for (const part of parts) {
                    if (part.includes('-')) {
                        const [rangeStart, rangeEnd] = part.split('-').map(num => parseInt(num.trim()));
                        if (isNaN(rangeStart) || isNaN(rangeEnd) || rangeStart > rangeEnd) {
                            throw new Error(`Invalid range format in skip numbers for rule ${rule.startUnitId}-${rule.endUnitId}: ${part}`);
                        }
                        for (let i = rangeStart; i <= rangeEnd; i++) {
                            skipSet.add(i);
                        }
                    } else {
                        const num = parseInt(part);
                        if (isNaN(num)) {
                            throw new Error(`Invalid number format in skip numbers for rule ${rule.startUnitId}-${rule.endUnitId}: ${part}`);
                        }
                        skipSet.add(num);
                    }
                }
            } catch (error) {
                // This error should ideally be caught by validateMappingRules, 
                // but we add a fallback here.
                setError(error.message);
                return null; // Mark rule as invalid
            }
        }
        return { ...rule, skipSet };
    }).filter(rule => rule !== null); // Filter out any rules that failed parsing
    
    // Check if pre-processing caused an error
    if (processedMappingRules.length !== unitNumberMappingRules.length) {
        // Error already set by the catch block above
        return;
    }

    // Prepare overrides map for quick lookup (Filter out incomplete overrides)
    const overridesMap = new Map();
    displayNameOverrides
      .filter(override => override.unitId && override.displayName)
      .forEach(override => {
        overridesMap.set(parseInt(override.unitId, 10), override.displayName);
      });

    const units = [];
    let unitsGenerated = 0;
    let currentUnitIdOffset = 0; // Offset from startUnitId

    // Store current number state for each rule to handle skips correctly across units
    const mappingRuleNumberState = new Map(processedMappingRules.map(rule => [rule.startUnitId, rule.startUnitNumber]));

    while (unitsGenerated < numberOfUnits) {
        const currentUnitId = startUnitId + currentUnitIdOffset;

        // Find the mapping rule for the current Unit ID
        const applicableRule = processedMappingRules.find(
            rule => currentUnitId >= rule.startUnitId && currentUnitId <= rule.endUnitId
        );

        if (!applicableRule) {
            // This should be caught by validateMappingRules, but adding a safeguard
            setError(`Error: No unit number mapping rule found for Unit ID ${currentUnitId}. Please ensure rules cover the entire range.`);
            setPreviewUnits([]); // Clear preview on error
            return;
        }

        // Get the current number for this rule block
        let currentUnitNumberForRule = mappingRuleNumberState.get(applicableRule.startUnitId);
        
        // Find the next available unit number for this rule, skipping if necessary
        while (applicableRule.skipSet.has(currentUnitNumberForRule)) {
            currentUnitNumberForRule++;
        }

        // Determine Display Name
        let finalDisplayName = '';
        if (overridesMap.has(currentUnitId)) {
            // 1. Check Overrides
            finalDisplayName = overridesMap.get(currentUnitId);
        } else {
            // 2. Check Prefix Rules (based on unitId)
            let prefix = '';
            const validPrefixRules = prefixRules.filter(rule => 
                typeof rule.startId === 'number' && typeof rule.endId === 'number' && rule.startId <= rule.endId
            );
            for (const rule of validPrefixRules) {
                if (currentUnitId >= rule.startId && currentUnitId <= rule.endId) {
                    prefix = rule.prefix || ''; 
                    break;
                }
            }

            // 3. Check Suffix Rules (based on unitId) 
            let suffix = '';
            const validSuffixRules = suffixRules.filter(rule =>
                typeof rule.startId === 'number' && typeof rule.endId === 'number' && rule.startId <= rule.endId
            );
            for (const rule of validSuffixRules) {
                if (currentUnitId >= rule.startId && currentUnitId <= rule.endId) {
                    suffix = rule.suffix || '';
                    break;
                }
            }

            // 4. Construct display name with prefix, *calculated* number, and suffix
            finalDisplayName = `${prefix}${currentUnitNumberForRule}${suffix}`;
        }

        // Determine Hardware ID
        const hardwareId = copyUnitIdToHardwareId ? currentUnitId.toString() : null;

        // Add unit
        units.push({
            unitNumber: currentUnitNumberForRule.toString(), // Use calculated number
            unitId: currentUnitId,
            displayName: finalDisplayName,
            hardwareId: hardwareId,
            status: status,
            isPrepaid: isPrepaid,
            siteName: siteName 
        });

        // Update the number state for the *next* unit in this rule block
        mappingRuleNumberState.set(applicableRule.startUnitId, currentUnitNumberForRule + 1);

        // Move to next unit overall
        unitsGenerated++;
        currentUnitIdOffset++;
    }
    
    setPreviewUnits(units);
  };
  
  // Validate that prefix rules make sense (now takes calculated range)
  const validatePrefixRules = (effectiveStartUnitId, effectiveEndUnitId) => {
    // Filter out incomplete rules first
    const validRules = prefixRules.filter(rule => 
        typeof rule.startId === 'number' && typeof rule.endId === 'number'
    );

    // If there are no valid prefix rules, that's fine
    if (validRules.length === 0) {
      return null;
    }
    
    // Check for rules where startId > endId
    for (const rule of validRules) {
      if (rule.startId > rule.endId) {
        return `Invalid prefix rule: Start ID (${rule.startId}) cannot be greater than End ID (${rule.endId})`;
      }
    }
    
    // Check for rules outside the effective unit ID range
    for (const rule of validRules) {
      if (rule.endId < effectiveStartUnitId || rule.startId > effectiveEndUnitId) {
        // This rule is completely outside the effective range, which is okay, just ignore for gap/overlap checks below.
        // However, maybe warn if it's *entirely* outside?
        // console.warn(`Prefix rule (${rule.startId}-${rule.endId}) is outside the effective unit range (${effectiveStartUnitId}-${effectiveEndUnitId})`);
      }
    }
    
    // Check for overlapping ranges within the effective range
    const rulesInEffectiveRange = validRules
        .map(rule => ({
            ...rule,
            // Clamp the rule range to the effective unit range for overlap/gap checks
            effectiveStart: Math.max(rule.startId, effectiveStartUnitId),
            effectiveEnd: Math.min(rule.endId, effectiveEndUnitId),
        }))
        .filter(rule => rule.effectiveStart <= rule.effectiveEnd); // Only consider rules that actually overlap the effective range

    for (let i = 0; i < rulesInEffectiveRange.length; i++) {
      const ruleA = rulesInEffectiveRange[i];
      for (let j = i + 1; j < rulesInEffectiveRange.length; j++) {
        const ruleB = rulesInEffectiveRange[j];
        
        // Check if effective ranges overlap
        if (ruleA.effectiveStart <= ruleB.effectiveEnd && ruleA.effectiveEnd >= ruleB.effectiveStart) {
           return `Overlapping prefix rules detected within the effective range: (${ruleA.startId}-${ruleA.endId}) and (${ruleB.startId}-${ruleB.endId})`;
        }
      }
    }
    
    // Check for gaps within the effective unit ID range if prefix rules are defined
    if (rulesInEffectiveRange.length > 0) {
      const sortedRules = rulesInEffectiveRange.sort((a, b) => a.effectiveStart - b.effectiveStart);
      
      let lastCovered = effectiveStartUnitId - 1;
      
      for (const rule of sortedRules) {
        // Check for a gap before the current rule starts
        if (rule.effectiveStart > lastCovered + 1) {
          // We only care about gaps *within* the effectiveStartUnitId to effectiveEndUnitId range
          const gapStart = Math.max(lastCovered + 1, effectiveStartUnitId);
          const gapEnd = Math.min(rule.effectiveStart - 1, effectiveEndUnitId);
          if (gapStart <= gapEnd) { // Ensure the gap is within the effective range and valid
             return `Gap in prefix rules: Unit IDs ${gapStart} to ${gapEnd} have no prefix defined (and no override).`;
          }
        }
        // Update the last covered ID, considering only the portion within the effective range
        lastCovered = Math.max(lastCovered, rule.effectiveEnd);
      }
      
      // Check for a gap after the last rule ends
      if (lastCovered < effectiveEndUnitId) {
          const gapStart = Math.max(lastCovered + 1, effectiveStartUnitId);
          return `Gap in prefix rules: Unit IDs ${gapStart} to ${effectiveEndUnitId} have no prefix defined (and no override).`;
      }
    }
    
    return null; // No validation errors
  };
  
  // --- New Suffix Validation Function ---
  const validateSuffixRules = (effectiveStartUnitId, effectiveEndUnitId) => {
    const validRules = suffixRules.filter(rule => 
        typeof rule.startId === 'number' && typeof rule.endId === 'number' && rule.suffix // Also ensure suffix exists
    );

    if (validRules.length === 0) {
        return null; // No rules, no errors
    }

    // Check for rules where startId > endId
    for (const rule of validRules) {
        if (rule.startId > rule.endId) {
            return `Invalid suffix rule: Start ID (${rule.startId}) cannot be greater than End ID (${rule.endId})`;
        }
    }

    // Check for rules outside the effective unit ID range (optional check, might just ignore)
    // ... (similar logic as prefix validation if needed)

    // Check for overlapping ranges within the effective range
    const rulesInEffectiveRange = validRules
        .map(rule => ({
            ...rule,
            effectiveStart: Math.max(rule.startId, effectiveStartUnitId),
            effectiveEnd: Math.min(rule.endId, effectiveEndUnitId),
        }))
        .filter(rule => rule.effectiveStart <= rule.effectiveEnd);

    for (let i = 0; i < rulesInEffectiveRange.length; i++) {
        const ruleA = rulesInEffectiveRange[i];
        for (let j = i + 1; j < rulesInEffectiveRange.length; j++) {
            const ruleB = rulesInEffectiveRange[j];
            if (ruleA.effectiveStart <= ruleB.effectiveEnd && ruleA.effectiveEnd >= ruleB.effectiveStart) {
                return `Overlapping suffix rules detected: (${ruleA.startId}-${ruleA.endId}) and (${ruleB.startId}-${ruleB.endId})`;
            }
        }
    }

    // Check for gaps (This might be less critical for suffixes than prefixes, 
    // as no suffix is often acceptable. Consider if you want to enforce full coverage).
    // If you want gap checking, uncomment and adapt the logic from validatePrefixRules:
    /*
    if (rulesInEffectiveRange.length > 0) {
      const sortedRules = rulesInEffectiveRange.sort((a, b) => a.effectiveStart - b.effectiveStart);
      let lastCovered = effectiveStartUnitId - 1;
      for (const rule of sortedRules) {
        if (rule.effectiveStart > lastCovered + 1) {
          const gapStart = Math.max(lastCovered + 1, effectiveStartUnitId);
          const gapEnd = Math.min(rule.effectiveStart - 1, effectiveEndUnitId);
          if (gapStart <= gapEnd) {
             return `Gap in suffix rules: Unit IDs ${gapStart} to ${gapEnd} have no suffix defined.`;
          }
        }
        lastCovered = Math.max(lastCovered, rule.effectiveEnd);
      }
      if (lastCovered < effectiveEndUnitId) {
          const gapStart = Math.max(lastCovered + 1, effectiveStartUnitId);
          return `Gap in suffix rules: Unit IDs ${gapStart} to ${effectiveEndUnitId} have no suffix defined.`;
      }
    }
    */

    return null; // No validation errors found
  };
  // --- End of Suffix Validation ---
  
  // --- New Unit Number Mapping Validation Function ---
  const validateMappingRules = (effectiveStartUnitId, effectiveEndUnitId) => {
    const rules = unitNumberMappingRules; // Use the state directly

    if (rules.length === 0 && numberOfUnits > 0) {
      return 'At least one Unit Number Mapping Rule is required.';
    }

    // Filter out incomplete rules first for validation purposes
    const validRules = rules.filter(rule => 
        typeof rule.startUnitId === 'number' && rule.startUnitId >= 1 &&
        typeof rule.endUnitId === 'number' && rule.endUnitId >= rule.startUnitId &&
        typeof rule.startUnitNumber === 'number' && rule.startUnitNumber >= 1
        // skipNumbers is optional text, validated during parsing if present
    );

    if (validRules.length !== rules.length) {
      // Find the first invalid rule to give a more specific error
      const invalidRuleIndex = rules.findIndex(rule => 
        !(typeof rule.startUnitId === 'number' && rule.startUnitId >= 1 &&
          typeof rule.endUnitId === 'number' && rule.endUnitId >= rule.startUnitId &&
          typeof rule.startUnitNumber === 'number' && rule.startUnitNumber >= 1)
      );
      return `Mapping Rule #${invalidRuleIndex + 1} has missing or invalid numeric values (Start/End Unit ID, Start Unit Number).`;
    }

    // Validate skipNumbers format within each rule
    for (let i = 0; i < validRules.length; i++) {
      const rule = validRules[i];
      if (rule.skipNumbers && rule.skipNumbers.trim()) {
        try {
          const parts = rule.skipNumbers.split(',').map(part => part.trim());
          for (const part of parts) {
            if (part.includes('-')) {
              const [rangeStart, rangeEnd] = part.split('-').map(num => parseInt(num.trim()));
              if (isNaN(rangeStart) || isNaN(rangeEnd) || rangeStart > rangeEnd) {
                throw new Error(`Invalid range format: ${part}`);
              }
            } else {
              const num = parseInt(part);
              if (isNaN(num)) {
                throw new Error(`Invalid number format: ${part}`);
              }
            }
          }
        } catch (error) {
          return `Mapping Rule #${i + 1} (IDs ${rule.startUnitId}-${rule.endUnitId}) has invalid skip numbers: ${error.message}`;
        }
      }
    }

    // Sort rules by startUnitId for overlap/gap checking
    const sortedRules = [...validRules].sort((a, b) => a.startUnitId - b.startUnitId);

    // Check for overlaps
    for (let i = 0; i < sortedRules.length - 1; i++) {
      if (sortedRules[i].endUnitId >= sortedRules[i+1].startUnitId) {
        return `Overlapping Unit Number Mapping Rules detected: Rule for IDs ${sortedRules[i].startUnitId}-${sortedRules[i].endUnitId} overlaps with rule for IDs ${sortedRules[i+1].startUnitId}-${sortedRules[i+1].endUnitId}.`;
      }
    }

    // Check for full coverage of the effective range
    if (sortedRules.length > 0) {
      // Check start coverage
      if (sortedRules[0].startUnitId > effectiveStartUnitId) {
        return `Gap in Unit Number Mapping: No rule covers Unit IDs starting from ${effectiveStartUnitId}.`;
      }
      // Check end coverage
      if (sortedRules[sortedRules.length - 1].endUnitId < effectiveEndUnitId) {
        return `Gap in Unit Number Mapping: No rule covers Unit IDs up to ${effectiveEndUnitId}.`;
      }
      // Check gaps between rules
      for (let i = 0; i < sortedRules.length - 1; i++) {
        if (sortedRules[i].endUnitId + 1 !== sortedRules[i+1].startUnitId) {
          return `Gap in Unit Number Mapping: No rule covers Unit IDs between ${sortedRules[i].endUnitId} and ${sortedRules[i+1].startUnitId}.`;
        }
      }
    } else if (numberOfUnits > 0) {
      // Only error if units are expected but no valid rules exist
       return 'No valid Unit Number Mapping Rules defined to cover the specified units.';
    }

    return null; // No validation errors
  };
  // --- End of Mapping Validation ---
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (previewUnits.length === 0) {
      setError('Please generate a preview first');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(false);
    setJobProgress(null);
    setPollingCount(0);
    
    try {
      // Submit all units at once using the bulk endpoint
      const response = await axios.post(
        `${API_URL}/units/bulk?siteUuid=${siteUuid}`,
        previewUnits.map(unit => ({
          unitNumber: unit.unitNumber,
          unitId: unit.unitId,
          displayName: unit.displayName,
          hardwareId: unit.hardwareId,
          status: unit.status,
          isPrepaid: unit.isPrepaid,
          siteName: unit.siteName
        })),
        {
          withCredentials: true // Using cookie-based authentication
        }
      );
      
      // Get the job ID from the response
      const jobId = response.data.jobId;
      setJobProgress(response.data);
      
      // Start polling for status updates
      pollJobStatus(jobId);
      
    } catch (err) {
      // console.error('Error creating units:', err);
      let errorMessage = 'Failed to create units';
      
      if (err.response) {
        // Server responded with an error status
        if (err.response.status === 401) {
          errorMessage = 'Your session has expired. Please log in again.';
        } else if (err.response.status === 403) {
          errorMessage = 'You do not have permission to perform this action. Admin or management role required.';
        } else if (err.response.data && err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };
  
  // Render job progress UI
  const renderJobProgress = () => {
    if (!jobProgress) return null;
    
    const isCompleted = jobProgress.status === 'COMPLETED';
    const isFailed = jobProgress.status === 'FAILED';
    const isPending = jobProgress.status === 'PENDING';
    const isProcessing = jobProgress.status === 'PROCESSING';
    
    // Calculate progress percentage
    const progressPercentage = jobProgress.totalUnits > 0
      ? Math.round((jobProgress.processedUnits / jobProgress.totalUnits) * 100)
      : 0;
    
    return (
      <div className="mt-6 bg-white rounded-lg p-6 shadow">
        <h3 className="text-lg font-semibold mb-4">Bulk Creation Job Status</h3>
        
        {/* Status indicator */}
        <div className="mb-4 flex items-center">
          <div className="mr-2">Status:</div>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            isCompleted ? 'bg-green-100 text-green-800' : 
            isFailed ? 'bg-red-100 text-red-800' : 
            'bg-blue-100 text-blue-800'
          }`}>
            {jobProgress.status}
            {(isPending || isProcessing) && (
              <span className="ml-2 inline-block animate-pulse">...</span>
            )}
          </div>
        </div>
        
        {/* Progress stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-gray-50 p-3 rounded">
            <div className="text-sm text-gray-500">Total</div>
            <div className="text-xl font-semibold">{jobProgress.totalUnits}</div>
          </div>
          
          <div className="bg-gray-50 p-3 rounded">
            <div className="text-sm text-gray-500">Processed</div>
            <div className="text-xl font-semibold">{jobProgress.processedUnits}</div>
          </div>
          
          <div className="bg-green-50 p-3 rounded">
            <div className="text-sm text-green-700">Successful</div>
            <div className="text-xl font-semibold text-green-700">{jobProgress.successfulUnits}</div>
          </div>
          
          <div className="bg-red-50 p-3 rounded">
            <div className="text-sm text-red-700">Failed</div>
            <div className="text-xl font-semibold text-red-700">{jobProgress.failedUnits}</div>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-1">
            <div>Progress</div>
            <div>{progressPercentage}%</div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div 
              className={`h-2.5 rounded-full ${
                isCompleted ? 'bg-green-600' : 
                isFailed ? 'bg-red-600' : 'bg-blue-600'
              }`}
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
        </div>
        
        {/* Loading animation for pending/processing */}
        {(isPending || isProcessing) && (
          <div className="flex justify-center my-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700"></div>
          </div>
        )}
        
        {/* Results list */}
        {jobProgress.results && jobProgress.results.length > 0 && (
          <div className="mt-4">
            <h4 className="font-medium mb-2">Results:</h4>
            <div className="max-h-60 overflow-y-auto border rounded">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {jobProgress.results.map((result, index) => (
                    <tr key={index} className={result.success ? '' : 'bg-red-50'}>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {result.unitNumber}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          result.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {result.success ? 'Success' : 'Failed'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-900">
                        {result.success ? '✓' : result.errorMessage}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold mb-2">Bulk Create Units</h1>
        <p className="text-gray-600">
          Create multiple units for site: {siteName || siteUuid}
        </p>
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          Units successfully created!
        </div>
      )}
      
      <div className="bg-white rounded-lg p-6 shadow mb-6">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h2 className="text-lg font-semibold mb-4">Unit Configuration</h2>
              
              <div className="grid grid-cols-2 gap-4"> {/* Changed to 2 cols */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Unit ID
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={startUnitId}
                    onChange={(e) => setStartUnitId(parseInt(e.target.value, 10) || 1)} // Ensure valid number
                    className="w-full px-3 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Number of Units
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={numberOfUnits}
                    onChange={(e) => setNumberOfUnits(parseInt(e.target.value, 10) || 1)} // Ensure valid number
                    className="w-full px-3 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* --- New Unit Number Mapping Rules Section --- */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-4">Unit Number Mapping Rules</h2>
            <p className="text-sm text-gray-600 mb-4">
              Define how Unit IDs map to Unit Numbers. Rules must cover the entire Unit ID range without overlaps.
            </p>
            
            {unitNumberMappingRules.map((rule, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-3 p-3 border border-gray-200 rounded">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Unit ID</label>
                  <input
                    type="number" min="1" placeholder="e.g., 1"
                    value={rule.startUnitId}
                    onChange={(e) => updateMappingRule(index, 'startUnitId', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Unit ID</label>
                  <input
                    type="number" min={rule.startUnitId || 1} placeholder="e.g., 20"
                    value={rule.endUnitId}
                    onChange={(e) => updateMappingRule(index, 'endUnitId', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Unit Number</label>
                  <input
                    type="number" min="1" placeholder="e.g., 101"
                    value={rule.startUnitNumber}
                    onChange={(e) => updateMappingRule(index, 'startUnitNumber', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Skip Unit Numbers</label>
                  <input
                    type="text" placeholder="e.g., 113, 120-125"
                    value={rule.skipNumbers}
                    onChange={(e) => updateMappingRule(index, 'skipNumbers', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md" />
                </div>
                <div className="flex items-end">
                  <button type="button" onClick={() => removeMappingRule(index)}
                    className="px-3 py-2 bg-red-100 text-red-600 rounded hover:bg-red-200">
                    Remove
                  </button>
                </div>
              </div>
            ))}
            
            <button type="button" onClick={addMappingRule}
              className="mt-2 px-4 py-2 bg-gray-100 text-gray-600 rounded hover:bg-gray-200">
              Add Mapping Rule
            </button>
          </div>
          {/* --- End of Mapping Rules Section --- */}
          
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-4">Display Name Prefixes (by Unit ID)</h2>
            <p className="text-sm text-gray-600 mb-4">
              Apply prefixes based on Unit ID ranges. Overrides below take precedence.
            </p>
            
            {prefixRules.length === 0 && (
              <div className="mb-4 p-4 bg-blue-50 text-blue-700 rounded">
                No prefix rules defined. Display names will default to Unit Number unless overridden.
              </div>
            )}
            
            {prefixRules.map((rule, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-3 p-3 border border-gray-200 rounded">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Unit ID {/* Label clarification */}
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={rule.startId}
                    onChange={(e) => updatePrefixRule(index, 'startId', parseInt(e.target.value, 10))}
                    className="w-full px-3 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Unit ID {/* Label clarification */}
                  </label>
                  <input
                    type="number"
                    min={rule.startId || 1} // Ensure min is valid
                    value={rule.endId}
                    onChange={(e) => updatePrefixRule(index, 'endId', parseInt(e.target.value, 10))}
                    className="w-full px-3 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prefix
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Floor_1_"
                    value={rule.prefix}
                    onChange={(e) => updatePrefixRule(index, 'prefix', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => removePrefixRule(index)}
                    className="px-3 py-2 bg-red-100 text-red-600 rounded hover:bg-red-200"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            
            <button
              type="button"
              onClick={addPrefixRule}
              className="mt-2 px-4 py-2 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
            >
              Add Prefix Rule
            </button>
          </div>
          
          {/* --- New Display Name Overrides Section --- */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-4">Display Name Overrides (by Unit ID)</h2>
            <p className="text-sm text-gray-600 mb-4">
              Set specific display names for individual Unit IDs. These override prefix rules.
            </p>
            
            {displayNameOverrides.map((override, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3 p-3 border border-gray-200 rounded">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit ID
                  </label>
                  <input
                    type="number"
                    min="1"
                    placeholder="e.g., 59"
                    value={override.unitId}
                    onChange={(e) => updateOverrideRule(index, 'unitId', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Exact Display Name
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., SQ1"
                    value={override.displayName}
                    onChange={(e) => updateOverrideRule(index, 'displayName', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => removeOverrideRule(index)}
                    className="px-3 py-2 bg-red-100 text-red-600 rounded hover:bg-red-200"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            
            <button
              type="button"
              onClick={addOverrideRule}
              className="mt-2 px-4 py-2 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
            >
              Add Override Rule
            </button>
          </div>
          {/* --- End of New Section --- */}
          
          {/* --- New Display Name Suffixes Section --- */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-4">Display Name Suffixes (by Unit ID)</h2>
            <p className="text-sm text-gray-600 mb-4">
              Append suffixes based on Unit ID ranges. Applied after prefixes, but overridden by exact display names.
            </p>
            
            {suffixRules.map((rule, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-3 p-3 border border-gray-200 rounded">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Unit ID
                  </label>
                  <input
                    type="number"
                    min="1"
                    placeholder="e.g., 1"
                    value={rule.startId}
                    onChange={(e) => updateSuffixRule(index, 'startId', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Unit ID
                  </label>
                  <input
                    type="number"
                    min={rule.startId || 1}
                    placeholder="e.g., 20"
                    value={rule.endId}
                    onChange={(e) => updateSuffixRule(index, 'endId', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Suffix
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., _A"
                    value={rule.suffix}
                    onChange={(e) => updateSuffixRule(index, 'suffix', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => removeSuffixRule(index)}
                    className="px-3 py-2 bg-red-100 text-red-600 rounded hover:bg-red-200"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            
            <button
              type="button"
              onClick={addSuffixRule}
              className="mt-2 px-4 py-2 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
            >
              Add Suffix Rule
            </button>
          </div>
          {/* --- End of Suffix Section --- */}
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="VACANT">Vacant</option>
              <option value="MAINTENANCE">Maintenance</option>
            </select>
          </div>
          
          {/* Is Prepaid */}
          <div className="mb-4">
            <label className="flex items-center text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={isPrepaid}
                onChange={(e) => setIsPrepaid(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
              />
              Is Prepaid
            </label>
            <p className="mt-1 text-sm text-gray-500">
              Check if all units in this bulk creation should be marked as prepaid
            </p>
          </div>
          
          {/* Copy Unit ID to Hardware ID */}
          <div className="mb-4">
            <label className="flex items-center text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={copyUnitIdToHardwareId}
                onChange={(e) => setCopyUnitIdToHardwareId(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
              />
              Copy Unit ID to Hardware ID
            </label>
            <p className="mt-1 text-sm text-gray-500">
              When checked, the Unit ID will be used as the Hardware ID for each unit
            </p>
          </div>
          
          <div className="flex justify-between mb-4">
            <button
              type="button"
              onClick={generatePreview}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Generate Preview
            </button>
            
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => navigate('/site-management')}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              
              <button
                type="submit"
                disabled={loading || previewUnits.length === 0}
                className={`px-4 py-2 ${
                  previewUnits.length === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-green-500 text-white hover:bg-green-600'
                } rounded`}
              >
                {loading ? 'Creating...' : 'Create Units'}
              </button>
            </div>
          </div>
        </form>
      </div>
      
      {/* Preview section */}
      {previewUnits.length > 0 && (
        <div className="bg-white rounded-lg p-6 shadow">
          <h2 className="text-lg font-semibold mb-4">Preview ({previewUnits.length} units)</h2>
          
          <div className="overflow-x-auto overflow-y-auto max-h-96 border border-gray-200 rounded">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Unit Number</th>
                  <th className="px-3 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Unit ID</th>
                  <th className="px-3 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Display Name</th>
                  <th className="px-3 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Hardware ID</th>
                  <th className="px-3 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-3 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Prepaid</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {previewUnits.map((unit, index) => (
                  <tr 
                    key={index}
                    className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                  >
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{unit.unitNumber}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{unit.unitId}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{unit.displayName}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{unit.hardwareId}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        unit.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                        unit.status === 'INACTIVE' ? 'bg-red-100 text-red-800' :
                        unit.status === 'VACANT' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {unit.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        unit.isPrepaid ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {unit.isPrepaid ? 'Yes' : 'No'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Job progress section */}
      {renderJobProgress()}
    </div>
  );
};

export default BulkCreateUnitsPage; 